// src/oura/ui/tokens.ts
//
// The Oura surface tokens: the six type sizes, the ink scale, the card
// surface, the product-area colours and every tone map the kit draws with.
//
// Values are the prototype's (parts/v16.head.part), written as Tailwind
// classes. Every hex that equals a Tailwind 3.4 default is written as that
// default; the rest (#F5F5F7 ground, the area colours, a few chip tints) are
// arbitrary values, literal here so the JIT sees them.
//
// These deliberately depart from the repo's fe-style guide on four points:
// sentence-case labels (no uppercase eyebrows), soft cards with no border,
// icons tinted by product area rather than always blue, and ink-filled
// filter pills. The redesign decided each one; do not "fix" them back.

import type { ClientPlanStatus } from "@service/aeo/clientReview";
import { MERCHANT_V2 } from "../merchantV2";
import type { CodeState, DocFlagCode, MerchantDocState } from "@service/aeo/clientDocsTypes";

/* ------------------------------------------------------------------------- *
 * Type: six sizes only (32 / 28 / 17 / 14 / 13 / 12), weights 400/500/600.
 * ------------------------------------------------------------------------- */

export const TYPE = {
    /** 32/38 semibold: the one page title. */
    h1: "text-[32px] leading-[38px] font-semibold tracking-[-0.02em]",
    /** 28/32 semibold, tabular: a tile's number. */
    num: "text-[28px] leading-8 font-semibold tracking-[-0.02em] tabular-nums",
    /** 17/24 semibold: a card title. */
    tt: "text-[17px] leading-6 font-semibold tracking-[-0.01em]",
    /** 17/26 regular ink2: the page sentence under the title. */
    ts: "text-[17px] leading-[26px] font-normal text-gray-600",
    /** 14/20: body. */
    tb: "text-sm",
    /** 13/18: meta. */
    tc: "text-[13px] leading-[18px]",
    /** 12/16: micro. */
    tm: "text-xs",
    /** 13/18 medium ink3: a sentence-case label. Never uppercase. */
    lab: "text-[13px] leading-[18px] font-medium text-gray-500",
} as const;

// The ink scale the kit's comments name: ink is text-gray-900 (#111827),
// ink2 text-gray-600, ink3 text-gray-500 and ink4 text-gray-400 (#9CA3AF).
// Components write the class itself; there is no lookup to go through.

/** A late note stays red wherever it sits, including on greyed rows. */
export const LATE_TEXT = "font-medium text-rose-600";

/** The page ground behind every redesigned screen. */
export const PAGE_GROUND = "bg-[#F5F5F7]";

export const SURFACE = {
    /** White, radius 16, a soft two-layer shadow and a 1px black/4 ring. No border. */
    card: "rounded-2xl bg-white shadow-[0_1px_2px_rgba(0,0,0,0.04),0_8px_24px_-12px_rgba(0,0,0,0.10)] ring-1 ring-black/[0.04]",
    /** The grey block inside a card (InnerStat, a quoted proposal). */
    inner: "rounded-xl bg-gray-50 px-3 py-2.5",
    /** The white block inside a drawer body. */
    block: "rounded-2xl bg-white p-4 ring-1 ring-black/[0.04]",
    /** Hairline between rows. */
    hairline: "border-gray-100",
    /** The firmer line between groups of rows (GroupFold). */
    divider: "border-gray-200",
} as const;

/**
 * The kind of leading visual a list's rows carry: a 32px IconSquare, a 14px
 * icon, or an 8px Dot. A GroupFold takes the same kind, so its title starts
 * where the rows' text starts.
 */
export type RowLead = "square" | "icon" | "dot";

/** The box each kind of leading visual sits in. */
export const LEAD_W: Record<RowLead, string> = { square: "w-8", icon: "w-3.5", dot: "w-2" };

/** Where a row's text starts: the lead's width plus the row's 12px gap. Literal, so the JIT sees each class. */
const INSET_LEFT: Record<RowLead, string> = { square: "before:left-11", icon: "before:left-[26px]", dot: "before:left-5" };

/**
 * The hairline above every row but the first, starting where the row's text
 * starts rather than under its icon, so a list reads as one column of text.
 * Drawn as a pseudo-element, so it takes no space and moves nothing.
 */
export function INSET_HAIRLINE(lead: RowLead): string {
    return `relative before:absolute before:right-0 before:top-0 before:h-px before:bg-gray-100 first:before:hidden ${INSET_LEFT[lead]}`;
}

/**
 * A row that opens something, as a whole (22 Oct 2026): a soft grey wash on
 * hover that reaches 12px past the row's edges, behind its content, so the
 * row reads as one target without moving a pixel. Uses the row's `after`, so
 * a row's `before` hairline is untouched.
 */
export const ROW_HOVER =
    "group relative isolate cursor-pointer after:pointer-events-none after:absolute after:-inset-x-3 after:inset-y-0 after:-z-10 after:rounded-xl after:bg-gray-100/80 after:opacity-0 after:transition-opacity after:content-[''] hover:after:opacity-100 motion-reduce:after:transition-none";

/** ROW_HOVER for a tight row with no padding of its own (a panel's lines): the wash reaches 6px above and below. */
export const ROW_HOVER_TIGHT = ROW_HOVER.replace("after:inset-y-0", "after:-inset-y-1.5");

/**
 * On a row's one action (a link or a pill): its hit area covers the whole
 * ROW_HOVER row, its nearest positioned parent.
 */
export const ROW_STRETCH = "before:absolute before:inset-0 before:content-['']";

/**
 * Inside a ROW_HOVER row: a control that keeps its own click (a Summary
 * toggle, a second link), above the row's stretched link.
 */
export const ROW_OWN = "relative z-10";

/** Keyboard focus, on every control the kit renders. */
export const FOCUS_RING = "focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600";

/**
 * A 44px tap target round a 28 or 32px pill, opt in per control: taller
 * rather than wider, so it never covers its neighbour. The pill looks the same.
 */
export const TAP_TARGET = "relative after:absolute after:-inset-x-1 after:-inset-y-2 after:content-['']";

/**
 * A pill drawn inside a tile or row that is itself the control ("Answer",
 * "Read", "Open"). A span, never a button: a button inside the row's button
 * is invalid markup and a second tab stop for the same action. `base` carries
 * no display class, so `tile` and `row` never put two display rules on one
 * element (`cn` joins, it does not merge).
 */
export const INLINE_PILL = {
    base: "h-7 shrink-0 items-center rounded-full px-3 text-xs font-medium",
    primary: "bg-blue-600 text-white",
    secondary: "bg-white text-gray-900 ring-1 ring-inset ring-black/[0.08]",
    /** On a tile the pill always shows. */
    tile: "inline-flex",
    /** On a row it gives way to the title under 640px; the dot and the chevron still say the row opens. */
    row: "hidden sm:inline-flex",
} as const;

/* ------------------------------------------------------------------------- *
 * Product areas: one colour per part of the product, for section icons.
 * ------------------------------------------------------------------------- */

/**
 * `plain` is not a product area: it is the absence of one, for surfaces where
 * the tint encodes nothing the reader can act on (#4062 MR-FE-4). The client
 * pages use it so that colour there means STATUS and only status; staff keep
 * the eight, where the area a screen belongs to is a real distinction across
 * forty-odd destinations.
 */
export type Area = "ai" | "google" | "site" | "work" | "dates" | "asks" | "people" | "setup" | "plain";

/** Hex values, for SVG strokes and anything that cannot take a class. */
export const AREA: Record<Area, string> = {
    ai: "#5856D6", // AI visibility
    google: "#007AFF", // Google search and traffic
    site: "#30B0C7", // the site
    work: "#34C759", // our work and what is done
    dates: "#FF9500", // dates and the plan
    asks: "#FF3B30", // asks and anything that needs someone
    people: "#AF52DE", // people and notes
    setup: "#8E8E93", // set up, documents
    plain: "#8E8E93", // no area: the icon is furniture, not a signal
};

/**
 * An icon's colour. Blue for anything that signals an area, gray for
 * furniture: one accent across Oura, so an icon never reads as a status the
 * way a red or purple one did. The hex above stays for charts, where the
 * series have to tell apart.
 */
export const AREA_TEXT: Record<Area, string> = {
    ai: "text-blue-600",
    google: "text-blue-600",
    site: "text-blue-600",
    work: "text-blue-600",
    dates: "text-blue-600",
    asks: "text-blue-600",
    people: "text-blue-600",
    setup: "text-gray-500",
    plain: "text-gray-400",
};

/* ------------------------------------------------------------------------- *
 * Navigation: one colour rule for every rail (staff, merchant, phone bar).
 * ------------------------------------------------------------------------- */

/**
 * A nav icon takes its row's colour: grey at rest, ourteam blue (#0B49FE,
 * brand book §5) on the page you are on. Never a product-area tint.
 *
 * The staff rail tinted each icon by area (the old NAV_TONE) after the
 * merchant rail had gone plain (#4062 MR-FE-2). Five tints on nine rows read
 * as status: a red Client looked like an error, grey Set up looked disabled,
 * and the active row paired a blue label with an indigo icon. The group
 * labels (Measure, Act, Set up) already say which part of the product a row
 * is in. Area colour stays inside the pages, where it helps the eye find a
 * card: PanelIcon, AreaIcon, tags and chips.
 */
export const NAV = {
    /** A press darkens the row at once, with no fade on the way in; hover eases. */
    rest: "text-gray-600 hover:bg-gray-50 hover:text-gray-900 active:bg-gray-100 active:duration-0",
    /** Text only. The wash is one element that slides from row to row (OuraSidebar). */
    active: "font-semibold text-[#0B49FE] active:bg-[#E1E9FE] active:duration-0",
    activeWash: "bg-[#F2F5FE]",
    /**
     * The phone bar: the icon square and the label under it. The wash is the
     * gliding mark (glide.ts), so the active square only tints. The label
     * keeps its weight when active, as a tab bar does: bold widened
     * "Visibility" past its 46px slot and cut it to "Visibil…".
     */
    barIconRest: "text-gray-500 group-active:bg-gray-100 group-active:duration-0",
    barIconActive: "text-[#0B49FE] group-active:bg-[#E1E9FE] group-active:duration-0",
    barLabelActive: "text-[#0B49FE]",
    /**
     * The business mark above the rail: neutral. It is the client's identity,
     * not ours, and under the OURA logo a second blue square read as two
     * brand marks stacked. Blue stays the one accent: the logo and the page
     * you are on.
     */
    mark: "bg-gray-100 text-gray-600",
} as const;

/* ------------------------------------------------------------------------- *
 * Tones: one vocabulary for tags, dots and icon squares.
 * ------------------------------------------------------------------------- */

export type Tone =
    | "blue"
    | "indigo"
    | "teal"
    | "green"
    | "orange"
    | "red"
    | "amber"
    | "purple"
    | "gray"
    | "violet"
    | "fuchsia"
    | "brown"
    | "rose"
    | "slate";

/** Tinted rounded-full tag: background, text and a 1px inset ring. */
export const TAG_TONE: Record<Tone, string> = {
    blue: "bg-blue-50 text-blue-700 ring-blue-200",
    indigo: "bg-indigo-50 text-indigo-700 ring-indigo-200",
    teal: "bg-[#ECFAFC] text-cyan-700 ring-[#A5E3EE]",
    green: "bg-emerald-50 text-emerald-700 ring-emerald-200",
    orange: "bg-[#FFF4E6] text-amber-700 ring-orange-200",
    red: "bg-[#FDECEC] text-[#C5221F] ring-red-200",
    amber: "bg-amber-50 text-amber-800 ring-amber-200",
    purple: "bg-[#F4EAFB] text-[#8E2DB8] ring-purple-200",
    gray: "bg-white text-gray-500 ring-gray-200",
    violet: "bg-violet-50 text-violet-700 ring-violet-200",
    fuchsia: "bg-[#FAEAFB] text-fuchsia-700 ring-fuchsia-200",
    brown: "bg-[#F3EEE8] text-[#7C5E3C] ring-[#E4D8CA]",
    rose: "bg-rose-50 text-rose-700 ring-rose-200",
    slate: "bg-slate-50 text-slate-600 ring-slate-200",
};

/** The 8px status dot. Amber = theirs, blue = ours in progress, green = done, gray = not started. */
export const DOT_TONE: Record<Tone, string> = {
    blue: "bg-blue-500",
    indigo: "bg-indigo-500",
    teal: "bg-[#30B0C7]",
    green: "bg-emerald-500",
    orange: "bg-orange-500",
    red: "bg-red-500",
    amber: "bg-amber-500",
    purple: "bg-purple-500",
    gray: "bg-gray-300",
    violet: "bg-violet-500",
    fuchsia: "bg-fuchsia-500",
    brown: "bg-[#A47148]",
    rose: "bg-rose-600",
    slate: "bg-slate-400",
};

/** 32px icon square: tinted ground, darker glyph. */
export const ICON_SQUARE_TONE: Record<Tone, string> = {
    blue: "bg-[#E8F1FF] text-[#0A5FD1]",
    indigo: "bg-[#EEEEFC] text-[#4745B8]",
    teal: "bg-[#E2F5F8] text-[#117A8B]",
    green: "bg-[#E7F7EC] text-[#1E7B38]",
    orange: "bg-[#FFF4E6] text-amber-700",
    red: "bg-[#FDECEC] text-[#C5221F]",
    amber: "bg-amber-50 text-amber-800",
    purple: "bg-[#F4EAFB] text-[#8E2DB8]",
    gray: "bg-gray-100 text-gray-500",
    violet: "bg-violet-50 text-violet-700",
    fuchsia: "bg-[#FAEAFB] text-fuchsia-700",
    brown: "bg-[#F3EEE8] text-[#7C5E3C]",
    rose: "bg-rose-50 text-rose-700",
    slate: "bg-slate-100 text-slate-600",
};

/** Question types: one colour each, none of them a status colour. */
export const KIND_TONE = {
    location: "bg-[#E3F4F8] text-cyan-700",
    specialty: "bg-[#FAEAFB] text-fuchsia-700",
    intent: "bg-[#ECEBFE] text-indigo-600",
    authority: "bg-[#F3EEE8] text-[#7C5E3C]",
    // The brand checks (Visibility Set v4.5). Written out whole, as Tailwind needs.
    pick: "bg-violet-50 text-violet-700",
    source: "bg-teal-50 text-teal-700",
    supplier: "bg-slate-100 text-slate-600",
} as const;

/** Catalogue families, shared with the Act mock: S blue, A indigo, B teal, G green, X (custom work) brown. */
export type Family = "s" | "a" | "b" | "g" | "x";
export const FAMILY_TONE: Record<Family, string> = {
    s: "bg-[#E8F1FF] text-[#0A5FD1]",
    a: "bg-[#EEEEFC] text-[#4745B8]",
    b: "bg-[#E2F5F8] text-[#117A8B]",
    g: "bg-[#E7F7EC] text-[#1E7B38]",
    x: "bg-[#F3EEE8] text-[#7C5E3C]",
};

/* ------------------------------------------------------------------------- *
 * Status words (D8). One place, so staff and merchant never drift.
 * ------------------------------------------------------------------------- */

export interface StatusStyle {
    label: string;
    tone: Tone;
    /** Not started: the row text goes ink4 and the dot grey. */
    muted?: boolean;
}

/** Staff groups work by who moves next. Late is rose text on any of them. */
export type StaffStatus = "in_progress" | "needs_client" | "done" | "not_started";
export const STAFF_STATUS: Record<StaffStatus, StatusStyle> = {
    in_progress: { label: "In progress", tone: "blue" },
    needs_client: { label: "Waiting on client", tone: "amber" },
    done: { label: "Done", tone: "green" },
    not_started: { label: "Not started", tone: "gray", muted: true },
};

/**
 * The merchant's plan states, keyed by the live `ClientPlanStatus`.
 *
 * With Google and Reported by hand are live-only states the prototype has no
 * word for. Neither is greyed: Reported by hand is work we account for by
 * hand (ourteam-backend#4814), not work that has not started, and the two
 * were the same pixel once already.
 */
export const MERCHANT_STATUS: Record<ClientPlanStatus, StatusStyle> = {
    waiting_on_you: { label: "Needs you", tone: "amber" },
    in_progress: { label: "In progress", tone: "blue" },
    // WITH GOOGLE IS NOT "NEEDS YOU". One is the client's to act on, the other
    // is a wait on a third party that they can do nothing about. They shared
    // amber, so the two states a client most needs to tell apart were the same
    // colour (#4062 MR-FE-4).
    with_google: { label: "With Google", tone: MERCHANT_V2 ? "violet" : "amber" },
    done: { label: "Done", tone: "green" },
    reported_by_hand: { label: "Reported by hand", tone: "slate" },
    not_started: { label: "Not started", tone: "gray", muted: true },
};
export const MERCHANT_STATUS_ORDER: ClientPlanStatus[] = [
    "waiting_on_you",
    "in_progress",
    "with_google",
    "done",
    "reported_by_hand",
    "not_started",
];

/* ------------------------------------------------------------------------- *
 * Documents (docs phase 1). The words are the scope's thread rules, verbatim;
 * the tones are the prototype's. Staff and merchant read the same map, so
 * the two sides cannot drift.
 * ------------------------------------------------------------------------- */

/** Every state a doc or a code can show, plus "No code yet" for a file with none in its name. */
export type DocStatusKey = CodeState | "no_code";

export interface DocStatusStyle {
    /** The staff word and tone. */
    staff: StatusStyle;
    /** The merchant word and tone, or null for a state the merchant never sees. */
    merchant: StatusStyle | null;
}

/**
 * Late is not its own tag: a late doc keeps its Waiting on client tag and
 * adds the late text ("3 working days late") in LATE_TEXT.
 */
export const DOC_STATUS: Record<DocStatusKey, DocStatusStyle> = {
    needs_us: { staff: { label: "Needs us", tone: "blue" }, merchant: { label: "Being revised", tone: "blue" } },
    waiting_on_client: { staff: { label: "Waiting on client", tone: "amber" }, merchant: { label: "Needs your reply", tone: "amber" } },
    late: { staff: { label: "Waiting on client", tone: "amber" }, merchant: { label: "Needs your reply", tone: "amber" } },
    not_sent_yet: { staff: { label: "To create", tone: "blue" }, merchant: null },
    approved: { staff: { label: "Approved", tone: "green" }, merchant: { label: "Answered", tone: "green" } },
    // Finished on our side, never shared (#4541): the client sees nothing of it.
    done: { staff: { label: "Done", tone: "green" }, merchant: null },
    not_shared: { staff: { label: "Not shared", tone: "gray", muted: true }, merchant: null },
    // "No questions", not "Nothing asked": on the Client tab "asked" is what the client does (Requests, YW 29 Sep 2026).
    nothing_asked: { staff: { label: "No questions", tone: "gray" }, merchant: { label: "Nothing to answer", tone: "gray" } },
    not_due: { staff: { label: "Not due yet", tone: "gray", muted: true }, merchant: null },
    no_code: { staff: { label: "No code yet", tone: "gray", muted: true }, merchant: null },
};

/** The staff board's groups, in the order they read: who moves next first. */
export const DOC_STATUS_ORDER: DocStatusKey[] = [
    "needs_us",
    "late",
    "waiting_on_client",
    "not_sent_yet",
    "approved",
    "done",
    "not_shared",
    "nothing_asked",
    "not_due",
    "no_code",
];

/** The merchant's own doc states (`MerchantDocument.state`). */
export const MERCHANT_DOC_STATUS: Record<MerchantDocState, StatusStyle> = {
    needs_your_reply: { label: "Needs your reply", tone: "amber" },
    with_us: { label: "Being revised", tone: "blue" },
    approved: { label: "Answered", tone: "green" },
    nothing_asked: { label: "Nothing to answer", tone: "gray" },
};

export interface DocFlagStyle {
    tone: "red" | "amber";
    /** The short fact for a tag on a row. The full sentence is the flag's `message`. */
    label: string;
}

/** Sharing flags are red, naming flags amber. A tag carries the fact; the fix goes in the drawer. */
export const DOC_FLAG: Record<DocFlagCode, DocFlagStyle> = {
    link_sharing: { tone: "red", label: "Anyone with the link" },
    edit_access: { tone: "red", label: "Client can edit" },
    internal_notes: { tone: "red", label: "Internal notes visible" },
    no_code: { tone: "amber", label: "No code" },
    name_title_disagree: { tone: "amber", label: "Name and title disagree" },
    code_not_in_contract: { tone: "amber", label: "Not in the contract" },
    word_file: { tone: "amber", label: "Word file" },
    comments_unreadable: { tone: "amber", label: "Comments not read" },
    resolved_without_agreement: { tone: "amber", label: "Resolved without their yes" },
};

/**
 * A client folder's state, in the same words on Connectors, All clients and
 * the Client tab's folder card, so no screen says "Linked" for a folder
 * another calls "Connected".
 */
export type FolderStatusKey = "not_linked" | "not_read" | "connected" | "no_access" | "read_failed" | "out_of_date";
export const FOLDER_STATUS: Record<FolderStatusKey, StatusStyle> = {
    not_linked: { label: "Not linked", tone: "gray" },
    not_read: { label: "Not read yet", tone: "gray" },
    connected: { label: "Connected", tone: "green" },
    no_access: { label: "No access", tone: "red" },
    read_failed: { label: "Read failed", tone: "red" },
    out_of_date: { label: "Out of date", tone: "amber" },
};

/** A failed folder read's state: No access when oura@ can't open it, Read failed for anything else. */
export function folderErrorStatus(code: string | null | undefined): "no_access" | "read_failed" {
    return code === "folder_not_readable" ? "no_access" : "read_failed";
}

/**
 * A change request's tag (the prototype's RST): open, answered, this month's
 * small change, done. Answered is green, as an answered question is in the
 * Questions to the client card's Answered group.
 */
export type RequestStatusKey = "open" | "answered" | "small_change" | "done";
export const REQUEST_STATUS: Record<RequestStatusKey, StatusStyle> = {
    open: { label: "Needs us", tone: "blue" },
    // Replied to and not done yet: still ours until someone marks it Done.
    answered: { label: "Answered, not done", tone: "blue" },
    small_change: { label: "This month's small change", tone: "violet" },
    done: { label: "Done", tone: "green" },
};
